import { memo, useMemo, useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import {
  I18n,
  Navigation,
  Container,
  Select,
  FormFooter,
  NavBar,
  CheckButtonList,
  FieldLabel,
  CheckButton,
} from '../../components';
import { THEME, COLOR, __WEB__, FONT } from '../../../constants/styles';
import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { convertDateToTZ } from '../../calendar/timezone_helper';

import { saveAppointmentSlotOverrides, resetStatus } from './slice';
import { getCurrentEmployerTimezone } from '../../../selectors/employers';
import { getCurrentEmployer } from '../../../selectors/entities';
import { AppointmentSelectedTimeSlot } from './common_views';

import moment from 'moment';

const NUM_OPTIONS = [{ label: 'None', id: 0 }].concat(
  [...Array(51).keys()].slice(1).map((id) => ({ label: id, id }))
);
const NUM_OPTIONS_ALL_SCHEDULES = [{ label: 'None', id: 0 }];

function startForOverride(time, selectedDate, timezone) {
  const timeStrs = (time || '00:00').split(':');
  const startDateLocal = new Date(selectedDate);
  startDateLocal.setHours(timeStrs[0]);
  startDateLocal.setMinutes(timeStrs[1]);
  startDateLocal.setSeconds(0);
  startDateLocal.setMilliseconds(0);
  const startDate = convertDateToTZ(startDateLocal, timezone);
  return startDate.toISOString();
}
function scheduleMatchesDateAndTime(schedule, date, time) {
  if (!schedule.days.includes(moment(date).isoWeekday())) return false;
  if (time && !schedule.slots.includes(time)) return false;
  if (moment(schedule.startDate).startOf('day').isAfter(moment(date))) return false;
  if (schedule.endDate && moment(schedule.endDate).endOf('day').isBefore(moment(date)))
    return false;
  return true;
}
const ProviderAvailabilityOverride = memo((props) => {
  useEffect(NavBar.setup(props), []);
  useEffect(function () {
    props.actions.resetStatus();

    return function () {
      props.actions.resetStatus();
    };
  }, []);

  const [num, setNum] = useState(0);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [scheduleSelections, setScheduleSelections] = useState([]);
  const [allSchedules, setAllSchedules] = useState(false);

  const editEvent = props.editEvent;
  const eventType = editEvent ? editEvent.eventTypeName : props.eventType;
  const selectedTimes = useMemo(() => {
    return editEvent ? [moment(editEvent.startDate).format('HH:mm')] : props.selectedTimes;
  }, [editEvent, props.selectedTimes]);
  const selectedDate = editEvent ? editEvent.startDate : props.selectedDate;

  const relevantSchedules = useMemo(() => {
    // first filer schedules by date
    const selectedDays = (props.selectedRange || [selectedDate]).map((date) =>
      moment(date).isoWeekday()
    );
    const rangeEnd = props.selectedRange?.length
      ? props.selectedRange[props.selectedRange.length - 1]
      : selectedDate;
    const rangeStart = props.selectedRange?.length ? props.selectedRange[0] : selectedDate;
    const selectedDaySchedules = props.schedules?.filter((s) => {
      if (moment(s.startDate).startOf('day').isAfter(moment(rangeEnd))) return false;
      if (s.endDate && moment(s.endDate).endOf('day').isBefore(moment(rangeStart))) return false;

      return selectedDays.some((day) => s.days.includes(day));
    });
    if (!selectedTimes?.length) {
      return selectedDaySchedules || [];
    } else {
      return (
        selectedDaySchedules?.filter((s) => {
          return selectedTimes.some((time) => s.slots.includes(time));
        }) || []
      );
    }
  }, [selectedTimes, props.schedules]);
  const scheduleOptions = useMemo(() => {
    return (
      relevantSchedules?.map((s) => ({
        label: s.name,
        id: s.employerSchedulerHoursId,
      })) || []
    );
  }, [relevantSchedules]);

  useEffect(() => {
    if (allSchedules) {
      setScheduleSelections(scheduleOptions);
      setNum(0);
    }
  }, [allSchedules, scheduleOptions]);
  useEffect(() => {
    if (scheduleSelections?.find((s) => s.checked)) {
      setAllSchedules(false);
    } else {
      // setAllSchedules(true);
    }
    const selectedSchedules = scheduleSelections?.filter((s) => s.checked).map((s) => s.id) || [];
    setSelectedSchedules(selectedSchedules);
    if (selectedSchedules.length) {
      if (num > 0) setNum(0);
    }
  }, [scheduleSelections]);
  useEffect(() => {
    setScheduleSelections(scheduleOptions);
  }, [scheduleOptions]);

  const numProviderOptions = useMemo(() => {
    if (selectedSchedules?.length === 1) {
      const selectedSchedule = relevantSchedules.find(
        (s) => s.employerSchedulerHoursId === selectedSchedules[0]
      );
      return NUM_OPTIONS.filter((o) => o.id !== selectedSchedule?.numProviders);
    }
    return NUM_OPTIONS_ALL_SCHEDULES;
  }, [selectedSchedules, relevantSchedules]);

  const handleCancel = useCallback(() => Navigation.close(props), []);
  const handleSave = useCallback(() => {
    const overrides = (
      !selectedTimes?.length
        ? (props.selectedRange?.length ? props.selectedRange : [selectedDate]).map((date) => {
            const startDate = startForOverride(null, date, props.timezone);
            return {
              allDay: true,
              startDate,
              date,
            };
          })
        : selectedTimes.map((time) => {
            const startDate = startForOverride(time, selectedDate, props.timezone);
            return {
              allDay: false,
              startDate,
              time,
              date: selectedDate,
            };
          })
    )
      .reduce((result, current) => {
        if (allSchedules) {
          result.push({
            ...current,
            numProviders: num,
            scheduleType: eventType,
          });
        } else {
          selectedSchedules.forEach((scheduleId) => {
            const schedule = relevantSchedules.find(
              (s) => s.employerSchedulerHoursId === scheduleId
            );

            if (scheduleMatchesDateAndTime(schedule, current.date, current.time)) {
              result.push({
                ...current,
                employerSchedulerHoursId: scheduleId,
                numProviders: num,
              });
            }
          });
        }
        return result;
      }, [])
      .map((o) => {
        const retO = { ...o };
        delete retO.time;
        delete retO.date;
        return retO;
      });
    props.actions.saveAppointmentSlotOverrides({
      employerId: props.employer?.employerId,
      overrides,
    });
  }, [
    selectedTimes,
    selectedDate,
    allSchedules,
    num,
    selectedSchedules,
    props.selectedRange,
    props.timezone,
    eventType,
  ]);

  const selectedTimeOrDays = !selectedTimes?.length ? (
    <Text>
      on your <Text style={THEME.textBold}>SELECTED DAYS</Text>
    </Text>
  ) : (
    <Text>
      on your <Text style={THEME.textBold}>SELECTED DAY</Text> and{' '}
      <Text style={THEME.textBold}>SELECTED TIMES</Text>
    </Text>
  );
  const description = useMemo(() => {
    if (!allSchedules && !selectedSchedules?.length) return null;
    return !!allSchedules ? (
      <Text style={THEME.textRegular}>
        Making this change will take <Text style={THEME.textBold}>ALL</Text> providers offline{' '}
        {selectedTimeOrDays}
      </Text>
    ) : !num && selectedSchedules?.length === 1 ? (
      <Text style={THEME.textRegular}>
        Making this change will take the <Text style={THEME.textBold}>SELECTED SCHEDULE</Text>{' '}
        offline {selectedTimeOrDays}
      </Text>
    ) : !num ? (
      <Text style={THEME.textRegular}>
        Making this change will take <Text style={THEME.textBold}>ALL SELECTED</Text> providers
        offline {selectedTimeOrDays}
      </Text>
    ) : (
      <Text style={THEME.textRegular}>
        Making this change will override the number of providers available for the{' '}
        <Text style={THEME.textBold}>SELECTED SCHEDULE</Text> {selectedTimeOrDays}
      </Text>
    );
  }, [num, allSchedules, selectedSchedules, selectedTimes, selectedTimeOrDays]);

  const showAllSchedulesOption = !selectedTimes?.length && !selectedSchedules?.length;

  const footer = (
    <FormFooter
      autoFocus={false}
      onCancel={handleCancel}
      cancelLabel={props.successMessage ? 'Done' : 'Cancel'}
      onSave={handleSave}
      saveDisabled={!(allSchedules || selectedSchedules?.length)}
    />
  );

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      renderActivityOverlay={props.status === REQUESTING || props.scheduleDataStatus === REQUESTING}
      errorMessage={props.error?.message || props.scheduleDataError?.message}
      successMessage={props.successMessage}
    >
      <View style={[styles.header]}>
        <AppointmentSelectedTimeSlot
          date={selectedDate}
          range={props.selectedRange}
          time={selectedTimes?.join(', ') || ''}
          location={props.location}
          eventType={props.eventType}
        />
      </View>
      <View style={{ alignItems: 'center' }}>
        <View style={styles.containerContent}>
          {!!showAllSchedulesOption && (
            <CheckButton
              checked={allSchedules}
              onChecked={setAllSchedules}
              label={'Take All Schedules Offline'}
              value={null}
              item={null}
              disabled={false}
              iconStyle={{ width: 28, height: 28 }}
              containerStyle={{ paddingLeft: 0, paddingBottom: 35 }}
              noBottomBorder={true}
              noTopBorder={true}
              labelStyle={{ ...FONT.regular, fontSize: 16 }}
            />
          )}

          {!!allSchedules ? null : (
            <>
              {!!showAllSchedulesOption && (
                <View style={[styles.orDivider, { paddingBottom: 30 }]}>
                  <View style={styles.orDividerLeft} />
                  <Text style={styles.orText}>OR</Text>
                  <View style={styles.orDividerRight} />
                </View>
              )}
              <View style={__WEB__ ? { minWidth: 200 } : null}>
                <FieldLabel label={'Select Schedules'} required={false} value={'Schedules'} />
                <CheckButtonList
                  items={scheduleSelections}
                  multi={true}
                  scrollable={false}
                  onValuesChanged={setScheduleSelections}
                  rowContainerStyle={{
                    backgroundColor: 'white',
                    borderBottomWidth: 0,
                    paddingVertical: 10,
                    paddingHorizontal: !showAllSchedulesOption ? 0 : 25,
                  }}
                  iconStyle={{ width: 28, height: 28 }}
                  containerStyle={{ paddingVertical: 10, paddingBottom: 15 }}
                  iconType={'check'}
                />
                {!!selectedSchedules?.length && (
                  <View style={__WEB__ ? { width: 200 } : null}>
                    <Select
                      required={true}
                      editable={selectedSchedules?.length === 1}
                      closeOnSelect={true}
                      options={numProviderOptions}
                      label={'Providers'}
                      value={num}
                      onSelect={setNum}
                      webMode={true}
                      menuPlacement="top"
                    />
                  </View>
                )}
              </View>
            </>
          )}
        </View>
      </View>
      {!!description && (
        <View style={styles.descriptionContainer}>
          <Text style={styles.desc}>{description}</Text>
        </View>
      )}
    </Container>
  );
});

const styles = StyleSheet.create({
  header: {
    flex: 1,
    borderBottomWidth: 1,
    borderColor: COLOR.accent,
    paddingVertical: 15,
    justifyContent: 'center',
  },
  headerLeft: false
    ? {
        flex: 0.5,
        borderRightWidth: 1,
        borderColor: COLOR.accent,
        paddingVertical: 15,
        paddingLeft: 50,
        minWidth: 450,
      }
    : {
        flex: 1,
        paddingLeft: 20,
        paddingTop: 30,
      },
  headerRight: __WEB__
    ? {
        flex: 0.5,
        alignItems: 'center',
        minWidth: 300,
      }
    : {
        flex: 0.5,
        alignItems: 'center',
        marginVertical: 30,
      },
  containerContent: {
    // alignItems: 'center',
    maxWidth: 400,
    minWidth: 200,
    paddingTop: 30,
    zIndex: 1000,
  },
  descriptionContainer: {
    borderTopWidth: 1,
    borderColor: COLOR.accent,
    paddingVertical: 40,
    paddingHorizontal: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contents: __WEB__
    ? {
        paddingTop: 40,
      }
    : {
        paddingTop: 40,
        paddingHorizontal: 25,
      },
  desc: {
    ...THEME.textRegular,
    marginRight: 10,
    minWidth: __WEB__ ? 500 : 200,
    maxWidth: 600,
  },
  num: {
    ...THEME.textBold,
  },

  orDivider: {
    ...THEME.basicRow,
  },
  orDividerLeft: {
    flex: 1,
    backgroundColor: 'black',
    height: 2,
  },
  orDividerRight: {
    flex: 1,
    backgroundColor: 'black',
    height: 2,
  },
  orText: {
    ...FONT.medium,
    color: 'black',
    fontSize: 16,
    marginHorizontal: 25,
  },
});

function mapStateToProps(state, ownProps) {
  const status = state.screens.scheduler.saveAptsSlotOverridesStatus;
  return {
    status,
    successMessage: status === SUCCESS && 'Provider Availability successfully updated.',
    error: state.screens.scheduler.saveAptsSlotOverridesError,
    employer: getCurrentEmployer(state),
    schedules: state.screens.scheduler.scheduleData,
    scheduleDataStatus: state.screens.scheduler.scheduleDataStatus,
    scheduleDataError: state.screens.scheduler.scheduleDataError,
    timezone: getCurrentEmployerTimezone(state),
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        saveAppointmentSlotOverrides,
        resetStatus,
      },
      dispatch
    ),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(ProviderAvailabilityOverride);
